import { usePopoverPortalContext } from "@circle-react-shared/uikit/PopoverPortal";
import { isIntendedOnNewTab } from "@circle-react/helpers/browserHelpers";
import type { Notification } from "@circle-react/types/Notification";
import { noop } from "lodash";

import { useNotificationContext } from "../NotificationContext";
import { useNotificationsActions } from "../useHeaderNotificationsApi";
import { NOTIFICATION_ACTION_MAPPER } from "./Item/constants";
import { CommentNotification } from "./NotifiableTypes/CommentNotification";
import { ContactNotification } from "./NotifiableTypes/ContactNotification";
import { DefaultNotification } from "./NotifiableTypes/DefaultNotification";
import { ModerationNotification } from "./NotifiableTypes/ModerationNotification";

export const NotificationItem = ({
  notification,
  lastMarkedAsRead,
}: {
  notification: Notification;
  lastMarkedAsRead: number | null;
}) => {
  const { onClose = noop } = usePopoverPortalContext() || {};
  const { onClose: onCloseDrawer } = useNotificationContext();
  const { mutate: performNotificationAction } = useNotificationsActions();

  const notificationUpdatedTimeStamp = new Date(
    notification.updated_at,
  ).getTime();
  const isMarkedAsRead = notification.read_at
    ? true
    : Boolean(
        lastMarkedAsRead && notificationUpdatedTimeStamp < lastMarkedAsRead,
      );

  const onClickHandler = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    performNotificationAction({
      id: notification.id,
      action: NOTIFICATION_ACTION_MAPPER.markAsRead,
    });
    if (isIntendedOnNewTab(event)) {
      return;
    }
    onCloseDrawer ? onCloseDrawer() : onClose();
  };

  const isContactNotification = notification.notifiable_type === "Contact";
  const isCommentNotification = notification.notifiable_type === "Comment";
  const shouldOpenPostInModal =
    notification.notifiable_type === "Post" &&
    (notification.action === "like" || notification.action === "add");
  const isModerationNotification =
    notification.notifiable_type === "FlaggedContent";

  if (isModerationNotification) {
    return (
      <ModerationNotification
        notification={notification}
        onClickHandler={onClickHandler}
        isMarkedAsRead={isMarkedAsRead}
      />
    );
  }
  if (isCommentNotification || shouldOpenPostInModal) {
    return (
      <CommentNotification
        notification={notification}
        onClickHandler={onClickHandler}
        isMarkedAsRead={isMarkedAsRead}
        shouldFocusOnCommentsSection={isCommentNotification}
      />
    );
  }

  if (isContactNotification) {
    return (
      <ContactNotification
        notification={notification}
        onClickHandler={onClickHandler}
        isMarkedAsRead={isMarkedAsRead}
      />
    );
  }

  return (
    <DefaultNotification
      notification={notification}
      onClickHandler={onClickHandler}
      isMarkedAsRead={isMarkedAsRead}
    />
  );
};
